import API_CONFIG from '../apiConfig';
const BASE_URL = API_CONFIG.BASE_URL;
const ASSETS_URL = 'https://www.emiratesscholar.com';

// Paper authors must arrive as a name string; never render raw author records
export const authorsText = (authors) => (typeof authors === 'string' ? authors : '');

// One random seed per visit: the server keeps the "random" member order stable
// across pages, so paging never repeats or skips members.
const NETWORK_SEED = Math.floor(Math.random() * 1000000000);

// ==================== دوال API الرئيسية ====================

/**
 * جلب صفحة واحدة من قائمة أعضاء الشبكة (server-side pagination)
 * @param {number} page  - رقم الصفحة (يبدأ من 1)
 * @param {string} search - نص البحث الاختياري
 * @returns {{ members, stats, total, totalPages, currentPage, perPage }}
 */
export const fetchMembersPage = async (page = 1, search = '') => {
  try {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('authToken');
    if (!token) throw new Error('يجب تسجيل الدخول أولاً');

    // Searches every member on the server, not just the members already loaded
    let url = `${BASE_URL}/network-members?page=${page}&seed=${NETWORK_SEED}`;
    if (search?.trim()) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      const errBody = await response.text().catch(() => '');
      console.error(`❌ [fetchMembersPage] HTTP ${response.status}:`, errBody);
      throw new Error(`فشل جلب أعضاء الشبكة: ${response.status}`);
    }

    const text = await response.text();
    if (text.startsWith('<!DOCTYPE') || text.startsWith('<html')) {
      throw new Error('الخادم أرجع HTML بدلاً من JSON');
    }

    const data = JSON.parse(text);
    return transformPageResponse(data);
  } catch (error) {
    console.error('❌ [fetchMembersPage] خطأ:', error);
    throw error;
  }
};

/**
 * جلب قائمة أعضاء الشبكة (legacy – page 1 only, kept for compatibility)
 */
export const fetchMembers = async (searchQuery = '') => {
  const result = await fetchMembersPage(1, searchQuery);
  return result;
};

/**
 * جلب تفاصيل عضو محدد عن طريق id
 */
export const fetchMemberById = async (memberId) => {
  try {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('authToken');
    if (!token) throw new Error('يجب تسجيل الدخول أولاً');

    const response = await fetch(`${BASE_URL}/my-network`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
      },
    });

    if (!response.ok) throw new Error('فشل جلب تفاصيل العضو');

    const text = await response.text();
    const data = JSON.parse(text);

    const usersList = data?.usersList?.data || [];
    const member = usersList.find(u => u.id === Number(memberId));
    if (!member) throw new Error('العضو غير موجود');

    return transformMemberDetailResponse(member);
  } catch (error) {
    console.error('❌ [fetchMemberById] خطأ:', error);
    throw error;
  }
};

// ==================== دوال تحويل البيانات ====================

/**
 * بناء رابط الصورة الكامل
 */
const getImageUrl = (imageName, imageLink) => {
  if (imageLink && imageLink.startsWith('http')) return imageLink;
  if (imageName && imageName.startsWith('http')) return imageName;
  if (imageLink) return `${ASSETS_URL}/portal/public/users/${imageLink}`;
  if (imageName) {
    if (imageName.includes('/')) return `${ASSETS_URL}/portal/public/users/${imageName}`;
    return `${ASSETS_URL}/portal/public/documents/${imageName}`;
  }
  return '';
};

/**
 * تحويل استجابة صفحة واحدة من API
 */
const transformPageResponse = (response) => {
  const usersList = response?.usersList;
  const membersList = usersList?.data || [];

  const members = membersList.map(user => ({
    id: user?.id,
    hasImage: !!(user?.user_details?.image || user?.user_details?.imageLink),
    name: [user?.name, user?.mname, user?.lname].filter(Boolean).join(' '),
    position: user?.user_details?.profession || '',
    company: user?.user_details?.organization || '',
    country: user?.user_details?.nationality || user?.user_details?.country || '',
    avatar: getImageUrl(user?.user_details?.image, user?.user_details?.imageLink),
    role: user?.user_details?.title || 'Member',
    verified: !!user?.email_verified_at,
    memberSince: user?.created_at ? new Date(user.created_at).getFullYear() : '',
    esid: `ESID ${user?.user_details?.membership || ''}`,
    bio: user?.user_details?.about || '',
    publications: (user?.user_publishes || []).map(pub => ({
      id: pub?.id,
      title: pub?.title || pub?.title_paper || pub?.paper_title || '',
      authors: authorsText(pub?.authors),
      journal: pub?.journal_name || '',
      date: pub?.publication_date || pub?.created_at || '',
    })),
    // Papers the member published outside Emirates Scholar
    externalPublications: user?.external_publications || [],
    stats: {
      publications: (user?.user_publishes?.length || 0) + (user?.external_publications?.length || 0),
      memberSince: user?.created_at ? new Date(user.created_at).getFullYear() : '',
      hIndex: 0,
    },
    chartData: { labels: [], values: [] },
  }));

  // API pagination metadata
  const total        = usersList?.total        || 0;
  const perPage      = usersList?.per_page     || membersList.length || 1;
  const currentPage  = usersList?.current_page || 1;
  const totalPages   = usersList?.last_page    || Math.ceil(total / perPage) || 1;

  const stats = {
    totalRegistered: formatNumber(total),
    yourNetwork: total,
    authors: total,
    newThisMonth: countNewThisMonth(membersList),
  };

  return {
    members,
    stats,
    total,
    totalPages,
    currentPage,
    perPage,
  };
};

/**
 * تحويل بيانات تفاصيل العضو
 */
const transformMemberDetailResponse = (user) => {
  const details = user?.user_details || {};
  const publications = user?.user_publishes || [];

  return {
    id: user?.id,
    name: [user?.name, user?.mname, user?.lname].filter(Boolean).join(' '),
    position: details?.profession || '',
    company: details?.organization || '',
    country: details?.nationality || details?.country || '',
    avatar: getImageUrl(details?.image, details?.imageLink),
    role: user?.user_type === 'speaker' ? 'Speaker' : details?.title || 'Member',
    verified: !!user?.email_verified_at,
    memberSince: user?.created_at ? new Date(user.created_at).getFullYear() : '',
    esid: `ESID ${details?.membership || ''} ${user?.created_at ? new Date(user.created_at).getFullYear() : ''}`,
    stats: {
      publications: publications.length,
      memberSince: user?.created_at ? new Date(user.created_at).getFullYear() : '',
      hIndex: 0,
    },
    chartData: {
      labels: ['2021', '2022', '2023', '2024', '2025'],
      values: [1, 4, 10, 2, 5],
    },
    bio: details?.about || '',
    education: [],
    experience: [{
      id: 1,
      position: details?.profession || '',
      company: details?.organization || '',
      country: details?.nationality || '',
      years: `${user?.created_at ? new Date(user.created_at).getFullYear() : ''} – Present`,
    }],
    publications: publications.map(pub => ({
      id: pub?.id || Math.random(),
      title: pub?.title || pub?.paper_title || '',
      authors: authorsText(pub?.authors),
      journal: pub?.journal_name || '',
      date: pub?.publication_date || pub?.created_at || '',
    })),
    // Papers the member published outside Emirates Scholar
    externalPublications: user?.external_publications || [],
  };
};

/**
 * حساب عدد الأعضاء الجدد هذا الشهر
 */
const countNewThisMonth = (membersList) => {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  return membersList.filter(member => {
    const d = new Date(member?.created_at);
    return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
  }).length;
};

/**
 * تنسيق الأرقام الكبيرة
 */
const formatNumber = (num) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)} M`;
  if (num >= 1000)    return `${(num / 1000).toFixed(1)} K`;
  return num?.toString() || '0';
};